import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaQrcode, FaTrash } from 'react-icons/fa';
import { formatBirthday, formatDate } from '../../../shared/utils/dateFormatters';
import { createLogger } from '../../../shared/config/logger';
import { getPlatformLabel, getPlatformBadgeClass } from '../../../shared/config/platformConfig';
import { CustomerQRModal } from './CustomerQRModal';

const logger = createLogger('CustomerTable');

/**
 * CustomerTable - Displays customers in a table (desktop) and card view (mobile)
 * @param {Object} props
 * @param {Array} props.customers - Array of customer objects
 * @param {Object} props.sortConfig - Current sort configuration { key: string, direction: 'asc' | 'desc' }
 * @param {Function} props.onSort - Callback when sort column is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 */

export function CustomerTable({ customers, sortConfig, onSort, onDelete, canDelete = true }) {
    const navigate = useNavigate();
    const [customerForQr, setCustomerForQr] = useState(null);

    const handleDeleteClick = (customer) => {
        logger.debug('Delete button clicked', { customer });
        onDelete(customer.wallet_pass_id);
    };

    const handleViewPass = (customer) => {
        logger.debug('View pass clicked', { customer });
        navigate(`/dashboard/scan?passId=${encodeURIComponent(customer.wallet_pass_id)}`);
    };

    const handleShowQr = (customer) => {
        logger.debug('QR button clicked', { customer });
        setCustomerForQr(customer);
    };

    const SortIndicator = ({ sortKey }) => {
        const isActive = sortConfig.key === sortKey;
        const isAsc = isActive && sortConfig.direction === 'asc';
        const isDesc = isActive && sortConfig.direction === 'desc';

        return (
            <span className="ml-1">
                <span className={isAsc ? 'font-bold text-primary' : 'text-gray-400'}>▲</span>
                <span className={isDesc ? 'font-bold text-primary' : 'text-gray-400'}>▼</span>
            </span>
        );
    };

    const PlatformBadge = ({ platform }) => {
        const platformClass = getPlatformBadgeClass(platform);
        const platformDisplay = getPlatformLabel(platform);

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformClass}`}>
                {platformDisplay}
            </span>
        );
    };

    return (
        <>
            {/* 🖥 Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full table-auto bg-white rounded-xl shadow text-xs sm:text-sm">
                    <thead className="bg-gray-100 text-gray-600">
                        <tr>
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Email</th>
                            <th className="p-3 text-left">Phone</th>
                            <th className="p-3 text-center">Platform</th>
                            <th
                                className="p-3 text-center cursor-pointer select-none"
                                onClick={() => onSort('total_points')}
                            >
                                Points
                                <SortIndicator sortKey="total_points" />
                            </th>
                            <th
                                className="p-3 text-center cursor-pointer select-none"
                                onClick={() => onSort('total_visits')}
                            >
                                Visits
                                <SortIndicator sortKey="total_visits" />
                            </th>
                            <th className="p-3 text-center">BirthDate</th>
                            <th
                                className="p-3 text-center cursor-pointer select-none"
                                onClick={() => onSort('customer_created_at')}
                            >
                                Created
                                <SortIndicator sortKey="customer_created_at" />
                            </th>
                            {canDelete && <th className="p-3 text-center">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {customers.map((customer, index) => (
                            <tr key={index} className="border-t text-center">
                                <td className="p-3 text-left">{customer.customer_name}</td>
                                <td className="p-3 text-left">{customer.customer_email}</td>
                                <td className="p-3 text-left">{customer.customer_phone}</td>
                                <td className="p-3">
                                    <PlatformBadge platform={customer.platform} />
                                </td>
                                <td className="p-3">{customer.total_points}</td>
                                <td className="p-3">{customer.total_visits}</td>
                                <td className="p-3">{formatBirthday(customer.customer_birthday)}</td>
                                <td className="p-3">
                                    {customer.customer_created_at
                                        ? new Date(customer.customer_created_at).toLocaleDateString()
                                        : '—'}
                                </td>
                                {canDelete && (
                                <td className="p-3">
                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => handleViewPass(customer)}
                                            className="text-sm bg-primary hover:opacity-90 text-white px-2 py-1 rounded"
                                        >
                                            View
                                        </button>
                                        <button
                                            onClick={() => handleShowQr(customer)}
                                            className="text-sm bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded"
                                        >
                                            QR
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(customer)}
                                            className="text-sm bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 📱 Mobile Card View */}
            <div className="block md:hidden space-y-4">
                {customers.map((customer, index) => (
                    <div key={index} className="border rounded-xl bg-white shadow-sm overflow-hidden">
                        <div className="p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="font-semibold text-primary text-base truncate flex-1 min-w-0">
                                    {customer.customer_name}
                                </h3>
                                <PlatformBadge platform={customer.platform} />
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-gray-700">
                                <span className="text-gray-500">Email</span>
                                <span className="truncate" title={customer.customer_email}>{customer.customer_email}</span>
                                <span className="text-gray-500">Phone</span>
                                <span>{customer.customer_phone || '—'}</span>
                                <span className="text-gray-500">Points</span>
                                <span className="font-medium text-primary">{customer.total_points}</span>
                                <span className="text-gray-500">Visits</span>
                                <span className="font-medium">{customer.total_visits}</span>
                                <span className="text-gray-500">Birth</span>
                                <span>{formatBirthday(customer.customer_birthday)}</span>
                                <span className="text-gray-500">Created</span>
                                <span>{formatDate(customer.customer_created_at)}</span>
                            </div>
                        </div>
                        <div className="flex border-t border-gray-100 bg-gray-50/50">
                            <button
                                type="button"
                                onClick={() => handleViewPass(customer)}
                                className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary hover:bg-primary/10 transition"
                                title="View pass"
                                aria-label="View pass"
                            >
                                <FaEye size={16} />
                                <span>View</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleShowQr(customer)}
                                className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 transition border-x border-gray-100"
                                title="Download pass QR"
                                aria-label="Download pass QR"
                            >
                                <FaQrcode size={16} />
                                <span>QR</span>
                            </button>
                            {canDelete && (
                                <button
                                    type="button"
                                    onClick={() => handleDeleteClick(customer)}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition"
                                    title="Delete"
                                    aria-label="Delete"
                                >
                                    <FaTrash size={16} />
                                    <span>Delete</span>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <CustomerQRModal
                isOpen={!!customerForQr}
                customer={customerForQr}
                onClose={() => setCustomerForQr(null)}
            />
        </>
    );
}